import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { ITenantConfig, ITenant, ITenantUIConfig, TenantProvider } from '../interface';
import { LocalTenantConfig } from '../index';
import { ITenantData, ViewIdEntity } from '../interface/ITenantData';
import {
  UserMetadata,
  ViewReference,
} from 'src/Common/manageViews/interfaces/UserMetadataResponse';
import { getViewsDataApi } from 'src/features/HubOverview/store/service/multitenantDashboardApi';
import {
  FolderViewChild,
  FolderViewResponse,
} from 'src/Common/manageViews/interfaces/ViewFolderResponse';
import { initial } from 'lodash-es';
import { rtkApi } from 'src/Common/RTK/api/rtkApi';
import { IUserContext } from 'src/manageAction/interfaces/ManageAction';
import { setUserProfile } from 'src/Common/manageViews/store/actions/userAction';
import {
  ICustomFilterQuery,
  IViewCustomFilterResponse,
} from 'src/Common/RTK/interface/CustomFilter';
import { artemisViewsApi } from 'src/features/HubOverview/store/service/artemisViewsApi';
import { DefaultConfigurationTenantId } from 'src/Common/manageViews/utils/ManageViewConfiguration';
import { MapIFieldDefinitionDTOToIFieldDefinition } from 'src/Common/manageViews/utils/helpers/MapIFieldDefinitionDTOToIFieldDefinition';

const tenantDataAdapter = createEntityAdapter<ITenantData>({
  selectId: (tenantData: ITenantData) => tenantData.tenantId, // Set the key for the entity to be tenant id
});

export interface ViewEntity {
  id: string;
}

const viewIdsAdapter = createEntityAdapter<ViewIdEntity>();
const systemViewIdsAdapter = createEntityAdapter<ViewIdEntity>(); //System views to fall to if default id is removed

interface TenantState {
  tenantConfig: ITenantConfig | null;
  tenantConfigByName: ITenantConfig | null;
  //tenantData: { [key: string]: ITenantData };
  tenantData: EntityState<ITenantData, string>;
}

const initialState: TenantState = {
  tenantConfig: null,
  tenantConfigByName: null,
  //tenantData: {},
  tenantData: tenantDataAdapter.getInitialState(),
};

const tenantSlice = createSlice({
  name: 'tenantConfiguration',
  initialState,
  reducers: {
    setTenantConfig: (state, action: PayloadAction<ITenantConfig>) => {
      // App.tsx dispatches the RAW /tenants payload keyed by TenantID. That payload lacks BOTH
      // the derived `provider` field AND the local UI config (FeatureConfig, nodeName,
      // TenantExperience, feature flags) that mergeTenantConfig attaches from LocalTenantConfig.
      // On a cold deep-link this dispatch can land after mergeTenantConfig and overwrite a good
      // merged entry. Writing the raw shape then causes two failures downstream:
      //   1. ProviderRedirection reads `provider === undefined` and wrongly takes the ADO branch
      //      for Artemis tenants -> "not migrated" error screen.
      //   2. If provider were only patched in (without the UI config), the detail page mounts and
      //      crashes on missing FeatureConfig/nodeName -> blank page.
      // So reproduce the SAME full merge mergeTenantConfig performs, per entry, so the written
      // shape is always complete.
      const previousConfig = state.tenantConfig ?? ({} as ITenantConfig);
      const nextConfig: ITenantConfig = { ...previousConfig };
      Object.entries(action.payload ?? {}).forEach(([tenantId, tenant]) => {
        const mutableTenant = { ...tenant };
        const localTenantConfig =
          mutableTenant && mutableTenant?.TenantID ? LocalTenantConfig[mutableTenant.TenantID] : undefined;

        //If /tenants API does not return field configurations, use the local field configurations
        if (mutableTenant.FieldConfigurations && mutableTenant.FieldConfigurations.length === 0) {
          const transformedFilterConfig = localTenantConfig?.FeatureConfig?.Hub?.FilterConfig?.map(
            (field) => {
              return {
                ...field,
                isFilterable: true,
              };
            }
          );
          mutableTenant.FieldConfigurations = transformedFilterConfig || [];
        }

        if (localTenantConfig) {
          nextConfig[tenantId] = {
            ...mutableTenant,
            ...(localTenantConfig as ITenantUIConfig),
            provider: mutableTenant.ArtemisCall ? TenantProvider.ARTEMIS : TenantProvider.ADO,
          };
        } else {
          // No local UI config for this tenant: keep any previously merged entry, otherwise fall
          // back to the raw payload without deriving an ARTEMIS provider, so we never mount a
          // detail page that would crash on the missing FeatureConfig/nodeName.
          nextConfig[tenantId] = previousConfig[tenantId] ?? mutableTenant;
        }
      });
      state.tenantConfig = nextConfig;
    },
    mergeTenantConfig: (state, action: PayloadAction<ITenant[]>) => {
      const tenants = Array.isArray(action.payload) ? action.payload : [];
      const { mergedConfig, mergedConfigByName } = tenants.reduce(
        (acc: { mergedConfig: ITenantConfig; mergedConfigByName: ITenantConfig }, tenant) => {
          // Create a mutable copy of the tenant to avoid mutating the frozen action payload
          const mutableTenant = { ...tenant };
          const tenantConfig =
            mutableTenant && mutableTenant?.TenantID ? LocalTenantConfig[mutableTenant.TenantID] : undefined;

          //If /tenants API does not return field configurations, use the local field configurations
          if (mutableTenant.FieldConfigurations && mutableTenant.FieldConfigurations.length === 0) {
            const transformedFilterConfig = tenantConfig?.FeatureConfig?.Hub?.FilterConfig?.map(
              (field) => {
                return {
                  ...field,
                  isFilterable: true,
                };
              }
            );
            mutableTenant.FieldConfigurations = transformedFilterConfig || [];
          }

          if (tenantConfig) {
            const localConfig = {
              ...mutableTenant,
              ...(tenantConfig as ITenantUIConfig),
              provider: mutableTenant.ArtemisCall ? TenantProvider.ARTEMIS : TenantProvider.ADO,
            };
            acc.mergedConfig[mutableTenant.TenantID] = localConfig;
            acc.mergedConfigByName[mutableTenant.Tenant] = localConfig;
          }
          return acc;
        },
        { mergedConfig: {} as ITenantConfig, mergedConfigByName: {} as ITenantConfig }
      ) || { mergedConfig: {}, mergedConfigByName: {} };
      state.tenantConfig = mergedConfig;
      state.tenantConfigByName = mergedConfigByName;

      const initialTenantDataList: ITenantData[] = [];
      Object.entries(mergedConfig).forEach((tenant) => {
        const initialTenantData: ITenantData = {
          tenantId: tenant[0],
          selectedViewId: null,
          defaultViewId: null,
          viewIds: viewIdsAdapter.getInitialState(),
        };
        initialTenantDataList.push(initialTenantData);
      });
      tenantDataAdapter.addMany(state.tenantData, initialTenantDataList);
    },
  },
});

export const { setTenantConfig, mergeTenantConfig } = tenantSlice.actions;

export default tenantSlice.reducer;
